/**
 * CablePathEditOverlay
 *
 * Renders cable path edit handles in a separate high-z-index layer
 * to ensure they're always clickable and on top of other elements.
 * Also renders a preview path line during vertex drag operations.
 */

import React, { useCallback, useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../../FloorPlanProvider';
import { CablePathEditHandles } from './CablePathEditHandles';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { useCablePathDrag } from '../../hooks/useCablePathDrag';
import { getFullPathPoints } from '@/services/cablePathService';
import { logger } from '@/lib/logger';
import type { Point } from '@/types/common';
import type { CablePath } from '@/types/cablePaths';

interface CablePathEditOverlayProps {
  containerRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
  imageSize: { width: number; height: number };
  isPublicView?: boolean;
}

export function CablePathEditOverlay({
  containerRef,
  zoom,
  position,
  imageSize,
  isPublicView = false,
}: CablePathEditOverlayProps) {
  // Get floor plan data
  const { floorPlan, scaleData } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;

  // Get cable data
  const { junctionCoordMap, moveJunction } = useCableJunctionsStandardized(floorPlanId);
  const { paths, updatePathGeometry } = useCablePathsStandardized(
    floorPlanId,
    junctionCoordMap,
    scaleData,
  );

  // Get store state
  const selectedCablePaths = useAppStore(state => state.selection.cablePaths);
  const activeMode = useAppStore(state => state.ui.toolbar.activeMode);
  const isDrawingMode = activeMode === 'cable-path-drawing';

  // Initialize path drag hook
  const pathDrag = useCablePathDrag({
    containerRef,
    zoom,
    position,
    paths,
    updatePathGeometry,
    moveJunction,
  });

  // Handle path edit handle drag start
  const handlePathEditDragStart = useCallback(
    (
      pathId: string,
      handleType: 'start' | 'end' | 'vertex',
      index: number,
      initialPos: Point,
      e: React.MouseEvent,
    ) => {
      logger.log('[CablePathEditOverlay] handlePathEditDragStart called', {
        pathId,
        handleType,
        index,
        initialPos,
      });
      if (!containerRef?.current || isPublicView || isDrawingMode) {
        logger.log('[CablePathEditOverlay] handlePathEditDragStart blocked by guard');
        return;
      }
      pathDrag.handleDragStart(pathId, handleType, index, initialPos, e);
    },
    [containerRef, isPublicView, isDrawingMode, pathDrag],
  );

  // Get selected paths
  const selectedPaths = useMemo(() => {
    return paths.filter(p => selectedCablePaths.has(p.id));
  }, [paths, selectedCablePaths]);

  // Compute preview path points during drag
  const getPreviewPathString = useCallback(
    (path: CablePath): string | null => {
      if (!pathDrag.isDragging || pathDrag.dragState?.pathId !== path.id) {
        return null;
      }

      const { handleType, vertexIndex, currentPos } = pathDrag.dragState;

      // Get base full points
      const fullPoints = getFullPathPoints(path, junctionCoordMap);
      if (fullPoints.length < 2) return null;

      // Create modified points array based on what's being dragged
      const modifiedPoints = [...fullPoints];

      if (handleType === 'vertex') {
        // Vertex drag: modify the intermediate point
        // fullPoints = [startJunction, ...intermediatePoints, endJunction]
        // path.points are the intermediate points (indices 0 to n-1)
        // In fullPoints, intermediate points are at indices 1 to (fullPoints.length - 2)
        const fullPointIndex = vertexIndex + 1; // Offset by 1 because index 0 is start junction
        if (fullPointIndex > 0 && fullPointIndex < modifiedPoints.length - 1) {
          modifiedPoints[fullPointIndex] = currentPos;
        }
      } else if (handleType === 'start') {
        // Start junction drag
        modifiedPoints[0] = currentPos;
      } else if (handleType === 'end') {
        // End junction drag
        modifiedPoints[modifiedPoints.length - 1] = currentPos;
      }

      // Build SVG path string
      if (modifiedPoints.length < 2) return null;
      const [first, ...rest] = modifiedPoints;
      return `M ${first.x} ${first.y} ${rest.map(p => `L ${p.x} ${p.y}`).join(' ')}`;
    },
    [pathDrag.isDragging, pathDrag.dragState, junctionCoordMap],
  );

  // Don't render if public view, drawing mode, or no selected paths
  if (isPublicView || isDrawingMode || selectedPaths.length === 0) {
    return null;
  }

  // SVG dimensions with padding
  const svgPadding = 5000;
  const svgWidth = (imageSize.width || 1000) + svgPadding * 2;
  const svgHeight = (imageSize.height || 1000) + svgPadding * 2;

  return (
    <svg
      style={{
        position: 'absolute',
        top: `-${svgPadding}px`,
        left: `-${svgPadding}px`,
        width: `${svgWidth}px`,
        height: `${svgHeight}px`,
        zIndex: 1000, // Very high z-index to be on top of everything
        overflow: 'visible',
        pointerEvents: 'none', // SVG container doesn't capture events
      }}
      data-edit-overlay="true"
    >
      <g transform={`translate(${svgPadding}, ${svgPadding})`} style={{ pointerEvents: 'auto' }}>
        {selectedPaths.map(path => {
          const previewPathString = getPreviewPathString(path);
          const isPathDragging = pathDrag.isDragging && pathDrag.dragState?.pathId === path.id;

          return (
            <g key={`overlay-${path.id}`}>
              {/* Preview path line during drag - fixed stroke width like main path */}
              {previewPathString && isPathDragging && (
                <path
                  d={previewPathString}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="6 3"
                  style={{ pointerEvents: 'none' }}
                />
              )}

              {/* Edit handles */}
              <CablePathEditHandles
                path={path}
                junctionCoordMap={junctionCoordMap}
                zoom={zoom}
                onDragStart={handlePathEditDragStart}
                isVisible={true}
                isDragging={isPathDragging}
                dragPreviewPos={pathDrag.getDragPreviewPos(
                  path.id,
                  pathDrag.dragState?.handleType || 'vertex',
                  pathDrag.dragState?.vertexIndex || 0,
                )}
                draggingHandle={
                  isPathDragging
                    ? {
                        type: pathDrag.dragState!.handleType,
                        index: pathDrag.dragState!.vertexIndex,
                      }
                    : null
                }
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}
